'use client';

import { c as _c } from "react/compiler-runtime";
import './payment-settings.scss';
import { useTranslation } from 'react-i18next';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { usePaymentMethodsWithCommitments } from '@sola/supporter-sdk/hooks/payment-methods.ts';
import { isCommitmentActiveRecurring } from '@sola/supporter-sdk/utils/commitments.ts';
import { LocalAlertsProvider } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import { LocalAlerts } from '../../molecules/local-alerts/local-alerts.tsx';
import { PaymentMethodCard } from '../../molecules/payment-method-card/payment-method-card.tsx';
import { PrimerError } from '../../molecules/primer-error/primer-error.tsx';
import { SectionContent } from '../../molecules/section-content/section-content.tsx';
import { PaymentIssueAlert } from '../payment-issue-alert/payment-issue-alert.tsx';
import { PaymentMethodAddSheet } from '../payment-method-add-sheet/payment-method-add-sheet.tsx';
import { AddPaymentMethodButton, AddPaymentMethodButtonLabel } from './payment-settings-add.tsx';
const useComponentData = () => {
  const $ = _c(6);
  const {
    isLoading,
    hasError,
    paymentMethods,
    commitmentsByPaymentMethodId,
    commitments
  } = usePaymentMethodsWithCommitments();
  let t0;
  if ($[0] !== commitments || $[1] !== commitmentsByPaymentMethodId || $[2] !== hasError || $[3] !== isLoading || $[4] !== paymentMethods) {
    t0 = {
      isLoading,
      hasError,
      paymentMethods,
      commitmentsByPaymentMethodId,
      commitments
    };
    $[0] = commitments;
    $[1] = commitmentsByPaymentMethodId;
    $[2] = hasError;
    $[3] = isLoading;
    $[4] = paymentMethods;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  return t0;
};
export const PaymentMethodList = () => {
  const $ = _c(49);
  const {
    t
  } = useTranslation("my");
  const {
    isLoading,
    hasError,
    paymentMethods,
    commitmentsByPaymentMethodId,
    commitments
  } = useComponentData();
  let t0;
  if ($[0] !== commitments || $[1] !== isLoading) {
    t0 = !isLoading && commitments.some(isCommitmentActiveRecurring);
    $[0] = commitments;
    $[1] = isLoading;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const hasActiveRecurringCommitments = t0;
  const showNoPaymentMethodsAlert = !hasError && paymentMethods.length === 0 && hasActiveRecurringCommitments;
  if (hasError && !paymentMethods?.length) {
    let t1;
    if ($[3] !== t) {
      t1 = t("paymentMethods.queryError.title", {
        count: 0
      });
      $[3] = t;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    let t2;
    if ($[5] !== t) {
      t2 = t("paymentMethods.queryError.message");
      $[5] = t;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== t) {
      t3 = t("ctas.contactSupport.url");
      $[7] = t;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== t) {
      t4 = t("ctas.contactSupport.text");
      $[9] = t;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== t3 || $[12] !== t4) {
      t5 = <a href={t3}>{t4}</a>;
      $[11] = t3;
      $[12] = t4;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    let t6;
    if ($[14] !== t1 || $[15] !== t2 || $[16] !== t5) {
      t6 = <Alert variant="negative" title={t1} message={t2} action={t5} />;
      $[14] = t1;
      $[15] = t2;
      $[16] = t5;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    return t6;
  }
  let t1;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <PrimerError keysToAlert={["commitments", "paymentMethods"]} />;
    $[18] = t1;
  } else {
    t1 = $[18];
  }
  let t2;
  if ($[19] !== isLoading) {
    t2 = !isLoading && <PaymentIssueAlert isSimple={true} onlyShowMissingPayment={true} />;
    $[19] = isLoading;
    $[20] = t2;
  } else {
    t2 = $[20];
  }
  let t3;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <LocalAlerts className="flex flex-col gap-comfy-sm" />;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  let t4;
  if ($[22] !== isLoading || $[23] !== showNoPaymentMethodsAlert || $[24] !== t) {
    t4 = !isLoading && showNoPaymentMethodsAlert && <Alert variant="warning" title={t("payments.alerts.noPaymentMethods.title")} message={<IntlBlock i18nKey="my:payments.alerts.noPaymentMethods.message" />} dismissible={false} />;
    $[22] = isLoading;
    $[23] = showNoPaymentMethodsAlert;
    $[24] = t;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  let t5;
  if ($[26] !== t) {
    t5 = t("payments.methods.heading");
    $[26] = t;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  let t6;
  if ($[28] !== commitments || $[29] !== commitmentsByPaymentMethodId || $[30] !== isLoading || $[31] !== paymentMethods) {
    t6 = !isLoading && !!paymentMethods?.length && paymentMethods.filter(_temp).map(method_0 => <PaymentMethodCard key={`payment-method-card-${method_0.id}`} paymentMethod={method_0} paymentMethodCommitments={commitmentsByPaymentMethodId?.[method_0.id] ?? []} allCommitments={commitments} />);
    $[28] = commitments;
    $[29] = commitmentsByPaymentMethodId;
    $[30] = isLoading;
    $[31] = paymentMethods;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  let t7;
  if ($[33] !== t) {
    t7 = t("payments.addPaymentMethod");
    $[33] = t;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  let t8;
  if ($[35] !== t7) {
    t8 = <AddPaymentMethodButton><AddPaymentMethodButtonLabel>{t7}</AddPaymentMethodButtonLabel></AddPaymentMethodButton>;
    $[35] = t7;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== commitments || $[38] !== t8) {
    t9 = <PaymentMethodAddSheet commitments={commitments} openTrigger={t8} />;
    $[37] = commitments;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  let t10;
  if ($[40] !== isLoading || $[41] !== t5 || $[42] !== t6 || $[43] !== t9) {
    t10 = <SectionContent heading={t5} isLoadingContent={isLoading}>{t6}{t9}</SectionContent>;
    $[40] = isLoading;
    $[41] = t5;
    $[42] = t6;
    $[43] = t9;
    $[44] = t10;
  } else {
    t10 = $[44];
  }
  let t11;
  if ($[45] !== t10 || $[46] !== t2 || $[47] !== t4) {
    t11 = <>{t1}{t2}{t3}{t4}{t10}</>;
    $[45] = t10;
    $[46] = t2;
    $[47] = t4;
    $[48] = t11;
  } else {
    t11 = $[48];
  }
  return t11;
};
export const PaymentSettings = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="PaymentSettings max-w-md flex flex-col gap-comfy-xl"><LocalAlertsProvider><PaymentMethodList /></LocalAlertsProvider></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
function _temp(method) {
  return !!method.id;
}